import React from "react";
import { useSelector } from "react-redux";
import CompanyTopPerformers from "./CompanyTopPerformers";

import { UseSelectedWorkspace } from "../../../hooks/company";
import {
  UseGroupAllKpis,
  UseGroupKpis,
  UseTopPlayers,
  useGetPreviousKpis,
  filterKpisByCreationDate,
  filterKpisByUserScores,
  isExcludedFromBoards,
} from "../../../hooks/kpi";
import { recomputeScopedPlayerScores } from "../../../helpers/kpiPoints";
import { filterKpisForPublicDisplay } from "../../../constants/kpi";
import { companyMonthStr } from "../../../helpers/companyTime";
import SoftBox from "../../SoftBox";
import SoftTypography from "../../SoftTypography";

function monthTodayKey() {
  return companyMonthStr();
}

/** Same column rules as Scorekeeper (ManualEntry displayedGroupKpis). */
function pastBoardColumnKpis({ monthRef, groupKpis, groupAllKpis, historicKpis }) {
  if (!monthRef) {
    return (groupKpis || []).filter((kpi) => !isExcludedFromBoards(kpi));
  }

  const monthKey = monthRef.format("YYYY-MM");
  const notHidden = (kpi) => !isExcludedFromBoards(kpi, monthKey);
  if (monthKey === monthTodayKey()) {
    return filterKpisByCreationDate(groupKpis, monthRef).filter(notHidden);
  }

  // Historic months: a metric having a score that month is proof it was active
  // then, so that alone decides the columns. We intentionally do NOT also apply
  // the creation-date filter here — a retired metric's stored `date` can be
  // unreliable and would wrongly drop it (e.g. "Baby Come Back" after it was
  // hidden). groupAllKpis includes disabled metrics, so their history survives.
  // Exact-title "Revenue" + retiredFrom cutoffs still drop board columns and
  // scoring so Yeti/player totals are not inflated.
  return filterKpisByUserScores(groupAllKpis, historicKpis).filter(notHidden);
}

const PastLeaderboard = ({ bigMode = true, monthRef }) => {
  const groupKpis = UseGroupKpis();
  const groupAllKpis = UseGroupAllKpis();
  const workspace = UseSelectedWorkspace();
  const { getPreviousKpis } = useGetPreviousKpis();

  const monthKey = monthRef?.format?.("YYYY-MM");

  // Past Boards must keep showing metrics that were later retired (disabled) for
  // the months they were still active — e.g. "Baby Come Back" (hidden Jul 2026)
  // or the old "Revenue Credit". Columns render off this catalog, so build it
  // from the active columns PLUS every disabled metric that still carries
  // history. This replaces the old per-id hardcodes so retiring any metric never
  // erases it from previous months. (The active "Revenue Credit" duplicate is
  // still excluded so it doesn't collide with its retired predecessor.)
  const allKpis = React.useMemo(() => {
    const active = (groupKpis || []).filter(
      (kpi) => kpi?._id !== "6a2ffe68a1042c14f1138e3e",
    );
    const activeIds = new Set(active.map((k) => String(k._id)));
    const retired = (groupAllKpis || []).filter(
      (k) => k?.disabled && !activeIds.has(String(k._id)),
    );
    // Same board exclude as columns — catalog fallback must not resurrect Revenue.
    return [...active, ...retired].filter(
      (kpi) => !isExcludedFromBoards(kpi, monthKey),
    );
  }, [groupKpis, groupAllKpis, monthKey]);

  const historicKpis = useSelector((state) =>
    monthKey ? state.kpiReducer.historicKpis?.[monthKey] : null,
  );

  const metricsReady =
    !monthKey || monthKey === monthTodayKey() || Array.isArray(historicKpis);

  const { columnKpis, scoringKpis } = React.useMemo(() => {
    const columns = pastBoardColumnKpis({
      monthRef,
      groupKpis,
      groupAllKpis,
      historicKpis,
    });
    // Scorekeeper-only metrics are hidden from the public columns but stay in
    // the scoring list so their points still count toward player totals —
    // same split as the live leaderboard and the TV.
    return { columnKpis: filterKpisForPublicDisplay(columns), scoringKpis: columns };
  }, [monthRef, groupKpis, groupAllKpis, historicKpis]);

  const rawUsersKpis = UseTopPlayers("month", monthRef);

  const usersKpis = React.useMemo(() => {
    if (!Array.isArray(rawUsersKpis)) return rawUsersKpis;
    const filtered = rawUsersKpis.filter(
      (player) =>
        !player?.isArchived &&
        player?.isActive !== false &&
        (player?.isYeti || (player?.scores?.total || 0) > 0),
    );
    if (!scoringKpis?.length) return filtered;
    const recomputed = filtered.map((player) => ({
      ...player,
      scores: recomputeScopedPlayerScores(player.scores, scoringKpis, {
        recomputeAnchorAsProgressive: true,
      }),
    }));
    return recomputed
      .filter((player) => player?.isYeti || (player.scores?.total || 0) > 0)
      .sort((a, b) => (b.scores?.total || 0) - (a.scores?.total || 0));
  }, [rawUsersKpis, scoringKpis]);

  React.useEffect(() => {
    if (monthRef && workspace?._id) {
      getPreviousKpis(monthRef, true);
    }
  }, [monthRef, workspace, columnKpis, getPreviousKpis]);

  return (
    <SoftBox
      className="max-w-full min-w-0 w-full"
      sx={{
        WebkitOverflowScrolling: "touch",
        width: "100%",
        minHeight: "200px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SoftBox
        className="flex flex-col mb-4 text-center w-full"
        sx={{ width: "100%", minWidth: 0 }}
      >
        <SoftBox
          className="flex justify-between items-center mb-4"
          sx={{ flexDirection: { mini: "column", sm: "row" } }}
        >
          <SoftTypography
            sx={{
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "24px",
              color: "#000000",
              marginBottom: "-2rem!important",
            }}
          >
            {workspace?.name} - <span style={{ color: "#98A2B3" }}>Top Performers</span>
          </SoftTypography>
        </SoftBox>
        <SoftBox sx={{ width: "100%", minWidth: 0, minHeight: 0, overflow: "visible" }}>
          <CompanyTopPerformers
            groupKpis={columnKpis}
            usersKpis={usersKpis}
            kpiCatalog={allKpis}
            metricsReady={metricsReady}
            bigMode={bigMode}
          />
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
};

export default PastLeaderboard;
